// Command palette (⌘K) + global search, shared by the user and admin panels.
// Registered as an Alpine data component so the markup in
// shared/_command_palette.html.erb can drive it declaratively. Static
// navigation "commands" are passed in from the layout (they hold route
// helpers); dynamic results (links / users / pages / domains) are fetched
// from the panel's own search endpoint as the user types.

export function registerCommandPalette(Alpine) {
  Alpine.data("commandPalette", (config = {}) => ({
    open: false,
    query: "",
    commands: config.commands || [],
    searchUrl: config.searchUrl || "",
    dynamicGroups: [],
    activeIndex: 0,
    loading: false,
    _debounce: null,
    _abort: null,

    init() {
      // Global ⌘K / Ctrl+K toggle, plus "/" as a quick-open when not typing.
      this._onKeydown = (e) => {
        const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
        if (cmdK) {
          e.preventDefault();
          this.toggle();
          return;
        }
        const inField = /^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable;
        if (e.key === "/" && !this.open && !inField) {
          e.preventDefault();
          this.openPalette();
        }
      };
      document.addEventListener("keydown", this._onKeydown);

      // Let anything on the page open the palette (e.g. the top-bar search
      // trigger, which lives in a separate Alpine root) via a custom event.
      this._onOpen = () => this.openPalette();
      window.addEventListener("cmdk:open", this._onOpen);

      this.$watch("query", () => this.onQueryChange());
    },

    destroy() {
      document.removeEventListener("keydown", this._onKeydown);
      window.removeEventListener("cmdk:open", this._onOpen);
    },

    toggle() {
      this.open ? this.closePalette() : this.openPalette();
    },

    openPalette() {
      this.open = true;
      this.query = "";
      this.dynamicGroups = [];
      this.activeIndex = 0;
      document.body.classList.add("overflow-hidden");
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },

    closePalette() {
      this.open = false;
      document.body.classList.remove("overflow-hidden");
      if (this._abort) this._abort.abort();
    },

    // ── Results ────────────────────────────────────────────────────────
    // Merge the (query-filtered) static commands with fetched dynamic groups,
    // stamping each item with a global index for keyboard navigation.
    get displayGroups() {
      const q = this.query.trim().toLowerCase();
      const cmds = q === ""
        ? this.commands
        : this.commands.filter((c) => `${c.title} ${c.keywords || ""}`.toLowerCase().includes(q));

      const merged = [];
      if (cmds.length) merged.push({ label: "Go to", items: cmds });
      for (const g of this.dynamicGroups) merged.push(g);

      let i = 0;
      return merged.map((g) => ({
        label: g.label,
        items: g.items.map((it) => ({ ...it, _i: i++ })),
      }));
    },

    get flatItems() {
      return this.displayGroups.flatMap((g) => g.items);
    },

    get hasResults() {
      return this.flatItems.length > 0;
    },

    onQueryChange() {
      this.activeIndex = 0;
      if (this._debounce) clearTimeout(this._debounce);

      const q = this.query.trim();
      if (q === "" || !this.searchUrl) {
        this.dynamicGroups = [];
        this.loading = false;
        return;
      }

      this.loading = true;
      this._debounce = setTimeout(() => this.fetchResults(q), 160);
    },

    async fetchResults(q) {
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      try {
        const res = await fetch(`${this.searchUrl}?q=${encodeURIComponent(q)}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: this._abort.signal,
        });
        if (!res.ok) throw new Error(`search failed: ${res.status}`);
        const data = await res.json();
        // Ignore a stale response whose query no longer matches the input.
        if (q !== this.query.trim()) return;
        this.dynamicGroups = Array.isArray(data.groups) ? data.groups : [];
        this.activeIndex = 0;
      } catch (err) {
        if (err.name !== "AbortError") this.dynamicGroups = [];
      } finally {
        this.loading = false;
      }
    },

    // ── Keyboard navigation ────────────────────────────────────────────
    move(delta) {
      const n = this.flatItems.length;
      if (n === 0) return;
      this.activeIndex = (this.activeIndex + delta + n) % n;
      this.$nextTick(() => {
        const el = this.$refs.list && this.$refs.list.querySelector(`[data-index="${this.activeIndex}"]`);
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },

    selectActive() {
      const item = this.flatItems[this.activeIndex];
      if (item) this.go(item);
    },

    go(item) {
      this.closePalette();
      const url = item.url;
      if (window.Turbo && url && url.startsWith("/")) {
        window.Turbo.visit(url);
      } else if (url) {
        window.location.href = url;
      }
    },

    // Single-path Heroicon `d` attributes, keyed by the icon name that either
    // a static command or a server result carries.
    icons: {
      dashboard: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
      link: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
      page: "M7 4h10a1 1 0 011 1v14a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1zm2 4h6m-6 4h6m-6 4h3",
      analytics: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      domain: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9",
      user: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
      users: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
      billing: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
      settings: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      developer: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
      team: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
      shield: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      plus: "M12 4v16m8-8H4",
      refund: "M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6",
      clicks: "M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    },

    iconPath(name) {
      return this.icons[name] || "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z";
    },
  }));
}
